import { memo } from "react";
import { Chip, Paper, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import type { ProviderInviteRow } from "../types";
import { ActivitySectionFrame } from "./ActivitySectionFrame";

const InviteRow = memo(function InviteRow({ row }: { row: ProviderInviteRow }) {
  const deadline = row.deadline ? dayjs(row.deadline) : undefined;
  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{
        p: 1.5,
        borderLeft: "4px solid",
        borderLeftColor: row.isPending ? "warning.main" : "divider",
        borderRadius: "0 8px 8px 0",
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {row.guestName}
          </Typography>
          <Chip
            size="small"
            label={row.statusLabel}
            color={row.isPending ? "warning" : "default"}
            variant={row.isPending ? "filled" : "outlined"}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Paciente: {row.patientName}
          {/* Data da consulta (o prazo do convite é o fim do atendimento). */}
          {row.consultationDate && ` · ${dayjs(row.consultationDate).format("DD/MM HH:mm")}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Solicitado por {row.requesterName} · {row.requesterOrganizationName}
        </Typography>
        {row.isPending && deadline?.isValid() && (
          <Typography variant="caption" color="text.secondary">
            Válido até {deadline.format("DD/MM HH:mm")}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
});

/**
 * Convites (Tasks) enviados pelas afiliadas para os profissionais da prestadora.
 * Só aparecem convites com o identifier da prestadora (criados a partir desta versão).
 * Os dados vêm da visão clínica (já filtrados por período e profissional).
 */
export const ProviderInvitesSection = memo(function ProviderInvitesSection({
  rows,
  isLoading,
  hasError,
  onRefresh,
}: {
  rows: ProviderInviteRow[];
  isLoading: boolean;
  hasError: boolean;
  onRefresh: () => void;
}) {
  return (
    <ActivitySectionFrame
      title="Convites para seus profissionais"
      subtitle="Pedidos de teleconsultoria e o status de cada convite."
      isLoading={isLoading}
      hasError={hasError}
      errorText="Não foi possível carregar os convites. Tente atualizar."
      isEmpty={!rows.length}
      emptyText="Nenhum convite de teleconsultoria neste período."
      onRefresh={onRefresh}
    >
      {rows.map((row) => (
        <InviteRow key={row.taskId} row={row} />
      ))}
    </ActivitySectionFrame>
  );
});
